import { useSyncExternalStore } from 'react';

/** Bản nháp đang mở, để sidebar biết mở khung chat cho cái nào. */
export interface PlanChatTarget {
  readonly projectId: string;
  readonly draftId: string;
}

/**
 * Nháp nào đang được xem — APM-ROLE-x2.
 *
 * ## Vì sao lại là một store ngoài React
 *
 * Cùng lý do với `aiRunStore` (APM-1302): bản nháp được **mở** từ sâu trong cây
 * (một hộp thoại trong tab Điều phối) nhưng khung chat được **hiển thị** ở
 * `AppShell`, ngoài mọi route. Context thì provider phải bọc cả hai, và mỗi lần
 * gõ một ký tự sẽ re-render toàn bộ cây bên dưới.
 *
 * ## Vì sao KHÔNG giữ nội dung hội thoại ở đây
 *
 * Lịch sử hỏi–đáp **đã nằm ở server**: `PlanDraftView.refinements` là
 * `[{ at, instruction, rationale }]`, tức đúng từng cặp "người hỏi / AI trả
 * lời". Dựng thêm một bản sao ở client là hai nguồn sự thật cho cùng một cuộc
 * hội thoại — và bản ở client sẽ mất trắng mỗi lần F5, đúng lúc người dùng
 * quay lại để đọc xem mình đã bàn gì.
 *
 * Store này vì thế chỉ giữ **con trỏ**: đang xem nháp nào. Nội dung do khung
 * chat tự đọc bằng query, dùng chung cache với màn hình nháp.
 */
class PlanChatStore {
  private target: PlanChatTarget | null = null;
  private readonly listeners = new Set<() => void>();

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): PlanChatTarget | null => this.target;

  open(target: PlanChatTarget): void {
    if (this.target?.draftId === target.draftId && this.target?.projectId === target.projectId) {
      return;
    }
    this.target = target;
    this.emit();
  }

  /**
   * Đóng khung chat. Nhận `draftId` để **không đóng nhầm**: hộp thoại cũ có thể
   * unmount *sau* khi hộp thoại mới đã mở (React tháo cây theo thứ tự của nó,
   * không theo thứ tự người dùng bấm), và một `close()` vô điều kiện ở đó sẽ
   * đóng đúng khung vừa mở ra.
   */
  close(draftId: string): void {
    if (this.target?.draftId !== draftId) return;
    this.target = null;
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}

export const planChatStore = new PlanChatStore();

export function usePlanChatTarget(): PlanChatTarget | null {
  return useSyncExternalStore(planChatStore.subscribe, planChatStore.getSnapshot);
}
